import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Buildings, EnvelopeSimple, ArrowRight, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
      if (data.token) {
        setResetToken(data.token);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-8">
            <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
            <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle weight="fill" className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-2">
                Link erstellt!
              </h1>
              <p className="text-slate-500 mb-6">
                Sie können Ihr Passwort jetzt zurücksetzen.
              </p>
              
              {resetToken && (
                <Link 
                  to={`/reset-password?token=${resetToken}`}
                  className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 flex items-center justify-center gap-2 mb-4"
                  data-testid="reset-password-link"
                >
                  Passwort jetzt zurücksetzen
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
              )}
              
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 hover:underline">
                Zurück zur Anmeldung
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-2">
                Passwort vergessen?
              </h1>
              <p className="text-slate-500 text-sm mb-6">
                Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="forgot-password-error">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                    E-Mail
                  </label>
                  <div className="relative">
                    <EnvelopeSimple className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                      placeholder="ihre@email.de"
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? 'Wird gesendet...' : 'Link anfordern'}
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </button>
              </form>

              <p className="mt-6 text-center">
                <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 hover:underline flex items-center justify-center gap-2" data-testid="back-to-login-link">
                  <ArrowLeft weight="bold" className="w-4 h-4" />
                  Zurück zur Anmeldung
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
